import React, { useContext } from 'react'

import { Navigate, useParams } from 'react-router-dom'

import { AuthContext } from '../context/AuthContext'
import { SessionContext } from '../context/SessionContext'
import { opensInWorkspace } from '../component/session/studentView/studentSurface'
import { ConsentProvider } from '../component/workspace/context/ConsentContext'

import { DemographicSurveyProvider } from '../component/workspace/context/DemographicSurveyContext'
import { SurveyProvider } from '../component/workspace/context/SurveyContext'
import { RegionProvider } from '../component/workspace/context/RegionContext'
import { TelemetryProvider } from '../component/workspace/context/TelemetryContext'
import { MouseEventsProvider } from '../component/workspace/context/MouseEventsContext'
import { CodeProvider } from '../component/workspace/context/CodeContext'
import { TerminalProvider } from '../component/workspace/context/TerminalContext'
import { ChatProvider } from '../component/workspace/context/ChatContext'
import { QuizProvider } from '../component/workspace/context/QuizContext'
import { useSessionJoin } from '../component/workspace/hooks/useSessionJoin'
import { useExperimentEntry } from '../component/session/experiment/useExperimentEntry'
import { conditionTagOf } from '../component/session/experiment/experimentConfig'
import ExperimentEntryScreen from '../component/session/experiment/ExperimentEntryScreen'
import Navbar from '../component/workspace/components/Navbar' 
import Workspace from '../component/workspace/Workspace'
import WaitingRoom from '../component/workspace/components/WaitingRoom'

import '../css/codeIssuePanel.scss'

const CLASS_DEPLOYMENT_SESSIONS = [654, 661, 643, 662, 663, 664, 665, 666, 669, 674, 675, 676, 677, 678]
const GATEWAY_SESSION = 664

const WorkspacePage = React.memo(() => {
  const { id: sessionId } = useParams()
  const { currentUser } = useContext(AuthContext)
  const { session, sessionLoading, missingSessionId } = useContext(SessionContext)
  // An experiment's link, or one of its conditions': the student is sent to the condition
  // they are placed in before anything is joined. SessionContext only has this route's row
  // once the student's session_id points here, so otherwise the hook fetches it - and also when
  // SessionContext found no such session (a guest who joined on a mistyped link), so the
  // student is told so instead of waiting for good.
  const entry = useExperimentEntry(sessionId, session, {
    fetchRow:
      Number(currentUser?.session_id) !== Number(sessionId) ||
      (missingSessionId != null && Number(missingSessionId) === Number(sessionId)),
  })
  const { joinFailed } = useSessionJoin(+sessionId, { enabled: entry.status === 'open' })

  if (entry.status !== 'open') return <ExperimentEntryScreen entry={entry} />

  // While the session loads, the Navbar keeps a student on the exercise (lockedToExercise: no
  // My Exercises, no wordmark link), as the entry screen's does. Staff keep today's Navbar.
  if (sessionLoading || (!session && sessionId) || (session && session.id !== +sessionId)) {
    return (
      <div className="home">
        <Navbar sessionId={null} lockedToExercise />
        <div className="container" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: 'calc(100vh - 64px)',
          color: '#6b7280'
        }}>
          Loading Session...
        </div>
      </div>
    )
  }

  // C++, SQL, Blockly, Audio, Pop Quiz and the legacy group types can't run here - a student
  // following an older /workspace link for one is sent to /session/:id, which can.
  // Instructors (Monitor's "Open Workspace") stay; opensInWorkspace is true for every study
  // session.
  const isStudentRole = currentUser?.role === 3 || currentUser?.role === 4
  if (isStudentRole && !opensInWorkspace(session)) {
    return <Navigate to={`/session/${sessionId}`} replace />
  }

  // Gateway: redirect from shared link (664) to assigned condition
  // Skip if returning for task 2 (usedAI param present)
  const usedAIParam = new URLSearchParams(window.location.search).get('usedAI')
  if (session?.id === GATEWAY_SESSION && !usedAIParam && currentUser?.id) {
    const targetSession = currentUser.id % 2 === 0 ? 663 : 666
    window.location.href = `/workspace/${targetSession}`
    return null
  }

  const isClassDeployment = CLASS_DEPLOYMENT_SESSIONS.includes(session?.id)
  // duration_minutes is the opt-in marker for the general timed-session flow (see
  // SessionLobby.jsx/models/session.js) - set the first time an instructor presses Start,
  // cleared on Reset. Sessions that have never used the timer keep it null and fall through
  // to the workspace exactly as before; only gate students who are waiting on a session the
  // instructor has actually chosen to run on a timer.
  const isTimedSession = session?.duration_minutes != null

  // An experiment condition's waiting room and workspace keep a student on the exercise too
  // (see above); every other session's Navbar stays as it was.
  const lockedToExercise = Boolean(conditionTagOf(session))

  // Pending — waiting room
  if (session?.status === 'pending' && (isClassDeployment || isTimedSession)) {
    return (
      <div className="home">
        <Navbar sessionId={session?.id} session={session} lockedToExercise={lockedToExercise} />
        <WaitingRoom session={session} />
      </div>
    )
  }

  // Active — normal workspace
  return (
    <div className="home">
      <Navbar sessionId={session?.id} session={session} lockedToExercise={lockedToExercise} />
      {/* useSessionJoin retried and still couldn't record this join server-side (session_id/
          user_session) - previously this failed completely silently, with the student's
          own UI proceeding as if nothing were wrong while their join was never actually
          saved. Not blocking - they can keep working - but they should know to refresh. */}
      {joinFailed && (
        <div style={{
          background: '#fef3c7', color: '#92400e', padding: '8px 16px',
          fontSize: '0.85rem', textAlign: 'center', borderBottom: '1px solid #fde68a',
        }}>
          We had trouble saving that you joined this session. You can keep working, but please refresh the page to make sure your progress is recorded.
        </div>
      )}
      <ConsentProvider>
        <DemographicSurveyProvider>
          <SurveyProvider>
            <QuizProvider>
              <RegionProvider>
                <TelemetryProvider>
                  <MouseEventsProvider>
                    <CodeProvider>
                      <TerminalProvider>
                        <ChatProvider>
                          <div className="container" style={{ height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
                            <Workspace />
                          </div>
                        </ChatProvider>
                      </TerminalProvider>
                    </CodeProvider>
                  </MouseEventsProvider>
                </TelemetryProvider>
              </RegionProvider>
            </QuizProvider>
          </SurveyProvider>
        </DemographicSurveyProvider>
      </ConsentProvider>
    </div>
  )
})

export default WorkspacePage